import React, { useState, useEffect, useCallback } from 'react';
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, Thermometer, MapPin, RefreshCw, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Weather code to icon mapping (WMO Weather interpretation codes)
const getWeatherIcon = (code, size = 'md') => {
  const sizeClass = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-10 w-10',
    xl: 'h-16 w-16'
  }[size];

  if (code === 0) return <Sun className={`${sizeClass} text-yellow-500`} />;
  if (code <= 3) return <Sun className={`${sizeClass} text-yellow-400`} />;
  if (code <= 49) return <Cloud className={`${sizeClass} text-gray-400`} />;
  if (code <= 59) return <CloudRain className={`${sizeClass} text-blue-300`} />;
  if (code <= 69) return <CloudRain className={`${sizeClass} text-blue-500`} />;
  if (code <= 79) return <CloudSnow className={`${sizeClass} text-blue-200`} />;
  if (code <= 89) return <CloudRain className={`${sizeClass} text-blue-600`} />;
  if (code <= 99) return <CloudLightning className={`${sizeClass} text-purple-500`} />;
  
  return <Cloud className={`${sizeClass} text-gray-400`} />;
};

const getWeatherDescription = (code) => {
  if (code === 0) return 'Clear sky';
  if (code <= 3) return 'Partly cloudy';
  if (code <= 49) return 'Foggy';
  if (code <= 59) return 'Drizzle';
  if (code <= 69) return 'Rain';
  if (code <= 79) return 'Snow';
  if (code <= 89) return 'Showers';
  if (code <= 99) return 'Thunderstorm';
  return 'Unknown';
};

const WeatherCard = ({ yards = [], userProfile = null, className = '' }) => {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [pollen, setPollen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('current');
  const [location, setLocation] = useState({ lat: null, lon: null, name: 'Getting location...' });
  const [locationCache, setLocationCache] = useState({}); // Cache geocoded locations

  // Geocode an address using Open-Meteo's geocoding API
  const geocodeAddress = useCallback(async (address, name) => {
    // Check cache first
    if (locationCache[name]) {
      return locationCache[name];
    }
    
    try {
      // Use Open-Meteo's free geocoding API
      const searchQuery = encodeURIComponent(address || name);
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${searchQuery}&count=1`
      );
      
      if (!response.ok) return null;
      
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const result = {
          lat: data.results[0].latitude,
          lon: data.results[0].longitude,
          name: data.results[0].name || name
        };
        // Cache the result
        setLocationCache(prev => ({ ...prev, [name]: result }));
        return result;
      }
    } catch (err) {
    }
    return null;
  }, [locationCache]);

  const fetchWeather = useCallback(async (lat, lon) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch weather data
      const weatherResponse = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
        `&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m` +
        `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,wind_speed_10m_max` +
        `&timezone=auto&forecast_days=16`
      );
      
      if (!weatherResponse.ok) throw new Error('Weather fetch failed');
      
      const data = await weatherResponse.json();
      
      setWeather({
        temp: Math.round(data.current.temperature_2m),
        feelsLike: Math.round(data.current.apparent_temperature),
        humidity: data.current.relative_humidity_2m,
        windSpeed: Math.round(data.current.wind_speed_10m),
        weatherCode: data.current.weather_code,
        unit: data.current_units.temperature_2m
      });
      
      const dailyForecast = data.daily.time.map((date, idx) => ({
        date,
        weatherCode: data.daily.weather_code[idx],
        tempMax: Math.round(data.daily.temperature_2m_max[idx]),
        tempMin: Math.round(data.daily.temperature_2m_min[idx]),
        precipProb: data.daily.precipitation_probability_max[idx] || 0,
        windMax: Math.round(data.daily.wind_speed_10m_max[idx])
      }));
      
      setForecast(dailyForecast);
      
      // Fetch pollen data (don't fail if unavailable)
      try {
        const pollenResponse = await fetch(
          `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}` +
          `&current=grass_pollen,birch_pollen,alder_pollen,ragweed_pollen,mugwort_pollen,olive_pollen`
        );
        
        if (pollenResponse.ok) {
          const pollenData = await pollenResponse.json();
          const current = pollenData.current || {};
          
          // Get pollen values
          const grassPollen = current.grass_pollen;
          const birdPollen = current.birch_pollen;
          const alderPollen = current.alder_pollen;
          const ragweedPollen = current.ragweed_pollen;
          const mugwortPollen = current.mugwort_pollen;
          const olivePollen = current.olive_pollen;
          
          // Calculate tree and weed pollen (max of available types)
          const treePollen = Math.max(
            birdPollen || 0, 
            alderPollen || 0, 
            olivePollen || 0
          ) || null;
          const weedPollen = Math.max(
            ragweedPollen || 0, 
            mugwortPollen || 0
          ) || null;
          
          // Get level based on thresholds (grains/m³)
          const getLevel = (value, thresholds) => {
            if (!value && value !== 0) return null;
            if (value < thresholds[0]) return { level: 'Low', color: 'text-green-400', bgColor: 'bg-green-500/20' };
            if (value < thresholds[1]) return { level: 'Moderate', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
            if (value < thresholds[2]) return { level: 'High', color: 'text-orange-400', bgColor: 'bg-orange-500/20' };
            return { level: 'Very High', color: 'text-red-400', bgColor: 'bg-red-500/20' };
          };
          
          const grassLevel = getLevel(grassPollen, [20, 50, 100]);
          const treeLevel = getLevel(treePollen, [15, 40, 80]);
          const weedLevel = getLevel(weedPollen, [10, 30, 60]);
          
          // Get overall level (highest severity)
          const levels = [grassLevel, treeLevel, weedLevel].filter(l => l);
          const levelOrder = { 'Low': 1, 'Moderate': 2, 'High': 3, 'Very High': 4 };
          const overallLevel = levels.length > 0 
            ? levels.reduce((max, curr) => 
                (levelOrder[curr.level] > levelOrder[max.level] ? curr : max)
              )
            : null;
          
          // Check if any pollen data is actually available (non-null, non-zero)
          const hasPollen = grassPollen !== null || treePollen !== null || weedPollen !== null;
          
          if (hasPollen) {
            setPollen({
              grass: grassPollen,
              grassLevel,
              tree: treePollen,
              treeLevel,
              weed: weedPollen,
              weedLevel,
              overall: overallLevel
            });
          } else {
            // Set pollen to 'unavailable' to show the message
            setPollen('unavailable');
          }
        } else {
          setPollen('unavailable');
        }
      } catch (pollenError) {
        setPollen('unavailable');
      }
      
      setLoading(false);
    } catch (err) {
      setError('Could not fetch weather data');
      setLoading(false);
    }
  }, []);

  // Get user's location from profile as fallback
  const getUserProfileLocation = useCallback(async () => {
    if (!userProfile) return null;
    
    // Check if user has GPS coordinates
    const lat = userProfile.gps_lat && userProfile.gps_lat !== 0 ? userProfile.gps_lat : null;
    const lon = userProfile.gps_lng && userProfile.gps_lng !== 0 ? userProfile.gps_lng : null;
    
    if (lat && lon) {
      const locationName = [userProfile.suburb, userProfile.state].filter(Boolean).join(', ') || 'Your Location';
      return { lat, lon, name: locationName };
    }
    
    // Try to geocode user's address
    const searchQuery = [userProfile.suburb, userProfile.state, userProfile.country].filter(Boolean).join(', ') 
      || userProfile.address;
    
    if (searchQuery) {
      const geocoded = await geocodeAddress(searchQuery, 'user_profile');
      if (geocoded) {
        const locationName = [userProfile.suburb, userProfile.state].filter(Boolean).join(', ') || geocoded.name;
        return { lat: geocoded.lat, lon: geocoded.lon, name: locationName };
      }
    }
    
    return null;
  }, [userProfile, geocodeAddress]);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      // Try user profile location as fallback
      getUserProfileLocation().then(userLoc => {
        if (userLoc) {
          setLocation(userLoc);
          fetchWeather(userLoc.lat, userLoc.lon);
        } else {
          setError('Geolocation not supported');
          setLoading(false);
        }
      });
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ lat: latitude, lon: longitude, name: 'Current Location' });
        
        // Get city name from timezone
        try {
          const geoResponse = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m&timezone=auto`
          );
          const geoData = await geoResponse.json();
          if (geoData.timezone) {
            const cityFromTz = geoData.timezone.split('/').pop().replace('_', ' ');
            setLocation(prev => ({ ...prev, name: cityFromTz }));
          }
        } catch {
          // Keep default name
        }
        
        fetchWeather(latitude, longitude);
      },
      async (err) => {
        // Use user profile location as fallback instead of Sydney
        const userLoc = await getUserProfileLocation();
        if (userLoc) {
          setLocation(userLoc);
          fetchWeather(userLoc.lat, userLoc.lon);
        } else {
          // Final fallback - Melbourne (more central Australia default)
          setLocation({ lat: -37.81, lon: 144.96, name: 'Melbourne (default)' });
          fetchWeather(-37.81, 144.96);
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [fetchWeather, getUserProfileLocation]);

  // Handle location selection change
  const handleLocationChange = useCallback(async (value) => {
    setSelectedLocation(value);
    
    if (value === 'current') {
      getCurrentLocation();
      return;
    }
    
    // Find the selected yard
    const yard = yards.find(y => y.id === value || y.name === value);
    if (!yard) return;
    
    // Build display name - use suburb/state if available, otherwise parse from location
    let displayName = yard.name;
    if (yard.suburb || yard.state) {
      displayName = [yard.name, yard.suburb, yard.state].filter(Boolean).join(', ');
    } else if (yard.location && yard.location !== yard.name) {
      // Try to extract suburb/state from location string (e.g., "137 Atlantic Drive, Keysborough, VIC 3977")
      const locationParts = yard.location.split(',').map(s => s.trim());
      if (locationParts.length >= 2) {
        // Take last 2-3 parts as location info
        const locationInfo = locationParts.slice(-2).join(', ');
        displayName = `${yard.name} - ${locationInfo}`;
      } else {
        displayName = `${yard.name} - ${yard.location}`;
      }
    }
    
    // Check if yard has coordinates (latitude/longitude or gps_lat/gps_lng)
    const lat = yard.latitude || (yard.gps_lat && yard.gps_lat !== 0 ? yard.gps_lat : null);
    const lon = yard.longitude || (yard.gps_lng && yard.gps_lng !== 0 ? yard.gps_lng : null);
    
    if (lat && lon) {
      setLocation({ lat, lon, name: displayName });
      fetchWeather(lat, lon);
      return;
    }
    
    // Try to geocode the yard's address, location, suburb, or name
    setLoading(true);
    const searchQuery = yard.address || yard.location || [yard.suburb, yard.state, yard.country].filter(Boolean).join(', ') || yard.name;
    const geocoded = await geocodeAddress(searchQuery, yard.name);
    
    if (geocoded) {
      // Update display name with geocoded location info if we didn't have it
      if (!yard.suburb && !yard.state && geocoded.name) {
        displayName = `${yard.name} - ${geocoded.name}`;
      }
      setLocation({ lat: geocoded.lat, lon: geocoded.lon, name: displayName });
      fetchWeather(geocoded.lat, geocoded.lon);
    } else {
      // Fallback to current location if geocoding fails
      setError(`Could not find location for "${yard.name}"`);
      setLoading(false);
    }
  }, [yards, getCurrentLocation, fetchWeather, geocodeAddress]);

  useEffect(() => {
    getCurrentLocation();
  }, []);

  if (loading) {
    return (
      <div className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white ${className}`}>
        <div className="flex items-center justify-center h-24">
          <RefreshCw className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading weather...</span>
        </div>
      </div>
    );
  }

  if (error && !weather) {
    return (
      <div className={`bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl p-4 text-white ${className}`}>
        <div className="text-center">
          <p className="text-sm opacity-80">{error}</p>
          <button 
            onClick={getCurrentLocation}
            className="mt-2 px-3 py-1 bg-white/20 rounded-lg text-sm hover:bg-white/30"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const todayForecast = forecast[0];
  const weekForecast = forecast.slice(1, 8);

  return (
    <div className={`bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-xl overflow-hidden shadow-lg ${className}`} data-testid="weather-card">
      <div className="p-4">
        {/* Location Dropdown */}
        <div className="mb-3">
          <Select value={selectedLocation} onValueChange={handleLocationChange}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white h-9 text-sm hover:bg-white/20">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-white/80" />
                <SelectValue placeholder="Select location" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current" className="text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-500" />
                  <span>📍 Current Location</span>
                </div>
              </SelectItem>
              {yards.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 border-t">
                    Apiaries
                  </div>
                  {yards.map((yard) => {
                    const hasCoords = (yard.latitude || (yard.gps_lat && yard.gps_lat !== 0));
                    // Get location info - prefer suburb/state, fallback to parsing location string
                    let locationInfo = [yard.suburb, yard.state].filter(Boolean).join(', ');
                    if (!locationInfo && yard.location) {
                      // Parse location string - take last 2 parts (e.g., "Keysborough, VIC 3977")
                      const parts = yard.location.split(',').map(s => s.trim());
                      if (parts.length >= 2) {
                        locationInfo = parts.slice(-2).join(', ');
                      } else {
                        locationInfo = yard.location;
                      }
                    }
                    return (
                      <SelectItem key={yard.id || yard.name} value={yard.id || yard.name} className="text-sm py-2">
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5">🐝</span>
                          <div>
                            <div className="font-medium">{yard.name}</div>
                            {locationInfo && (
                              <div className="text-xs text-stone-500">{locationInfo}</div>
                            )}
                            {!hasCoords && !locationInfo && (
                              <div className="text-xs text-amber-500">📍 No location set</div>
                            )}
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Location Display */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-white/90 text-sm font-medium">
            {location.name}
          </div>
          <button 
            onClick={() => handleLocationChange(selectedLocation)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh weather"
          >
            <RefreshCw className="h-4 w-4 text-white/70" />
          </button>
        </div>

        {/* Current Weather */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getWeatherIcon(weather.weatherCode, 'xl')}
            <div>
              <div className="text-4xl font-bold text-white">
                {weather.temp}°
              </div>
              <div className="text-sm text-white/80">
                {getWeatherDescription(weather.weatherCode)}
              </div>
            </div>
          </div>
          
          <div className="text-right text-white/80 text-sm space-y-1">
            <div className="flex items-center gap-1 justify-end">
              <Thermometer className="h-3.5 w-3.5" />
              <span>Feels {weather.feelsLike}°</span>
            </div>
            <div className="flex items-center gap-1 justify-end">
              <Droplets className="h-3.5 w-3.5" />
              <span>{weather.humidity}%</span>
            </div>
            <div className="flex items-center gap-1 justify-end">
              <Wind className="h-3.5 w-3.5" />
              <span>{weather.windSpeed} km/h</span>
            </div>
          </div>
        </div>

        {/* Pollen Count Section */}
        {pollen && pollen !== 'unavailable' && pollen.overall && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-medium flex items-center gap-1.5">
                <span>🌾</span> Pollen Count
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${pollen.overall.bgColor} ${pollen.overall.color}`}>
                {pollen.overall.level}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {pollen.grassLevel && (
                <div className={`text-center p-1.5 rounded-lg ${pollen.grassLevel.bgColor}`}>
                  <div className="text-white/70">Grass</div>
                  <div className={`font-medium ${pollen.grassLevel.color}`}>{pollen.grassLevel.level}</div>
                </div>
              )}
              {pollen.treeLevel && (
                <div className={`text-center p-1.5 rounded-lg ${pollen.treeLevel.bgColor}`}>
                  <div className="text-white/70">Tree</div>
                  <div className={`font-medium ${pollen.treeLevel.color}`}>{pollen.treeLevel.level}</div>
                </div>
              )}
              {pollen.weedLevel && (
                <div className={`text-center p-1.5 rounded-lg ${pollen.weedLevel.bgColor}`}>
                  <div className="text-white/70">Weed</div>
                  <div className={`font-medium ${pollen.weedLevel.color}`}>{pollen.weedLevel.level}</div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Pollen Not Available Notice */}
        {!loading && pollen === 'unavailable' && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="flex items-center gap-1.5 text-white/60 text-xs">
              <span>🌾</span>
              <span>Pollen data not available for this region</span>
            </div>
          </div>
        )}

        {/* Today's High/Low */}
        {todayForecast && (
          <div className="mt-3 pt-3 border-t border-white/20 flex items-center justify-between text-white/90 text-sm">
            <span>Today</span>
            <span>
              H: {todayForecast.tempMax}° / L: {todayForecast.tempMin}°
              {todayForecast.precipProb > 0 && (
                <span className="ml-2 text-blue-200">
                  💧 {todayForecast.precipProb}%
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* 7-Day Forecast */}
      <div className="bg-black/20 px-4 py-3">
        <div className="flex justify-between items-center">
          {weekForecast.map((day, idx) => {
            const date = new Date(day.date);
            const dayName = idx === 0 ? 'Tom' : date.toLocaleDateString('en', { weekday: 'short' }).slice(0, 3);
            return (
              <div key={day.date} className="text-center text-white/90">
                <div className="text-xs mb-1">{dayName}</div>
                {getWeatherIcon(day.weatherCode, 'sm')}
                <div className="text-xs mt-1 font-medium">{day.tempMax}°</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { getWeatherIcon, getWeatherDescription };
export default WeatherCard;
